import { Ref, ref, reactive, watch } from 'vue';
import { searchDatasets, getDatasetTags } from '@/services/data/data-download';
import { getRequestParamByIdOrName, writeUrlRequest } from '@/config/common';

// type MediaTagType = {
//   documentMap: Record<string, string>
//   subjectMap: Record<string, string>
//   mainTypeMap: Record<string, string>
// };
// /* get Tag API return */
// type MediaTallyType = {
//   tags: MediaTagType,
//   year: Record<number, number>
// };
/* indiviudal API media object */
export class DatasetItem {
  name?: string;
  id?: string;
  datasetCreatedBy?: string[];
  description?: string;
  fullTitle?: string;
  publicationDate?: string;
  subjectAreas?: string[];
  years?: string[];
  tags?: string[];
  dataType?: string[];
  version?: string;

  constructor (data: any) {
    this.name = data.name;
    this.datasetCreatedBy = data.datasetCreatedBy;
    this.description = data.description;
    this.fullTitle = data.fullTitle;
    this.publicationDate = data.publicationDate;
    this.subjectAreas = data.subjectAreas;
    this.tags = data.tags;
    this.version = data.version;
  }
}

interface IDatasetItem {
  name?: string;
  id?: string;
  datasetCreatedBy?: string[];
  description?: string;
  fullTitle?: string;
  publicationDate?: string;
  subjectAreas?: string[];
  years?: string[];
  tags?: string[];
  dataType?: string[];
  version?: string;
}
type DatasetQueryResultType = {
  totalPage?: number;
  startPage?: number;
  recordPerPage?: number;
  totalRecord?: number;
  items?: IDatasetItem[]; // Use the interface here
};

export type DatasetItemType = typeof DatasetItem;

/* API return object */
// type DatasetQueryResultType = {
//   totalPage?: number,
//   startPage?: number,
//   recordPerPage?: number
//   totalRecord?: number
//   items?: DatasetItemType[]
// };
/* UI Stats */
type DatasetStatsType = {
  subjectAreas?: Record<number, number>,
  // region: t.union([RegionTS, t.undefined]),
//   category?: Record<number, number>,
  page?: {
    totalPage?: number,
    startPage?: number,
    recordPerPage?: number
    totalRecord?: number
  }
};

export type QueryParameterType = {
    searchText?:string | null,
    searchKey?:string[] | null,
    rootPath?: string | null,
    itemPerPage: number,
    tags?: string[] | null,
    dataType?: string[] | null,
    years?: number[] | null,
    subjectAreas?: string[] | null,
    publicationDate?: string | null,
    page: number | null, /* start Page */
    sortBy?: number | null
};

export const FEATURED_POST_SIZE = 2;
export const numFeatured = 2;

const defaultRecordPerPage = 6;
const appState = reactive({
  inFilterTerm: '', /* this is for holding user input, before they click enter or search button, just to save api calls when users enter partial words */
  pageNumber: 1,
  recordPerPage: defaultRecordPerPage
});

export const defaultQueryParam:QueryParameterType = {
  searchKey: null,
  rootPath: '/',
  itemPerPage: defaultRecordPerPage,
  page: 1, /* start Page */
  sortBy: 1
};
export type tagOptionType = {
  dataType: Record<string, number>[];
  subjectAreas: Record<string, number>[];
  tags: Record<string, number>[];
};
/* must be array, if not get the data and change them into array */
export const tagOptions:tagOptionType = {
  dataType: [],
  subjectAreas: [],
  tags: []
};

export type requestKeys = keyof QueryParameterType;
type optionKeys = keyof tagOptionType;

/**
 * read query parameters from url passing request string
 * @returns query object
 */
function readRequest ():QueryParameterType {
  const requestParam:QueryParameterType = { ...defaultQueryParam };
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);

  Object.keys(tagOptions).forEach(key => getRequestParamByIdOrName(tagOptions, urlParams, requestParam, key as optionKeys));
  requestParam.searchText = urlParams.get('searchText') ?? null;
  const tags = urlParams.get('tags');
  requestParam.tags = tags ? tags.split(',') : null;
  requestParam.subjectAreas = urlParams.get('subjectAreas') ? urlParams.get('subjectAreas')?.split(',') : null;
  requestParam.dataType = urlParams.get('dataType') ? urlParams.get('dataType')?.split(',') : null;
  requestParam.page = +(urlParams.get('page') ?? 1);
  // if (urlParams.get('year') != null) {
  //     requestParam.year = parseInt(urlParams.get('year') ?? '0');
  // }

  return requestParam;
}
export function useAppState () {
  return appState;
}

function app () {
  const state = useAppState();
  const dataList: Ref<IDatasetItem[]> = ref([]);
  const queryParam:Ref<QueryParameterType> = ref(readRequest()); /** clone from default */
  const dataStats:Ref<DatasetStatsType> = ref({});

  const tagOptions = ref();
  const yearOptions = ref();
  // Init loads anything needed at startup
  const init = async () => {
    const data = await getDatasetTags();
    Object.keys(data).forEach(key => {
      // data[key] = data[key].filter((x: string) => x?.length > 0).sort((a: string, b: string) => a.toString().toLowerCase() > b.toString().toLowerCase() ? 1 : -1).map((item: string) => ({ id: item, name: item }));
      data[key] = Object.keys(data[key]).map(x => {
        return { id: x, name: x, note: data[key][x] };
      });
      data[key].sort((a: Record<string, string>, b: Record<string, string>) => a.id.toString().toLowerCase() > b.id.toString().toLowerCase() ? 1 : -1);
    });
    tagOptions.value = data;
    await query();
  };

  const clearAllFilter = () => {
    queryParam.value = { ...defaultQueryParam };
  };

  const resetToSingleFilter = (fieldKey: requestKeys, value: string | string []) => {
    queryParam.value = { ...defaultQueryParam, [fieldKey]: value };
  };
  /* clear the whole query item, if it is array set it as empty, */
  const clearQueryItem = (fieldKey:requestKeys) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (queryParam.value[fieldKey] as any) = null;
  };
  const updatePage = (pageNumber:number) => {
    queryParam.value.page = Math.max(1, pageNumber);
  };

  watch(queryParam, () => {
    query();
  }, { deep: true });

  const query = async () => {
    writeUrlRequest(queryParam.value, ['itemPerPage', 'tags', 'subjectAreas', 'dataType', 'orderBy', 'rootPath', 'searchText']);
    const data = await searchDatasets(queryParam.value) as DatasetQueryResultType;
    dataList.value = data.items ?? [];
    dataStats.value.page = {
      startPage: queryParam.value?.page ?? 1,
      totalPage: data.totalPage,
      totalRecord: data.totalRecord ?? 0,
      recordPerPage: queryParam.value?.itemPerPage ?? 20
    };
  };
  const updateFilter = () => {
    queryParam.value.page = 1;
  };

  /* remove on value from array */
  const removeSingleQueryItem = (list:Array<number> | null, index:number) => {
    if (list) {
      list?.splice(index, 1);
    }
  };
  init();
  return {
    state,
    tagOptions,
    yearOptions,
    queryParam,
    updatePage,
    updateFilter,
    clearQueryItem,
    clearAllFilter,
    resetToSingleFilter,
    removeSingleQueryItem,
    query,
    dataList,
    dataStats

  };
}
const appSingleton = app();
export function dataDownloadSearchState () {
  // NOTE: Any setup hooks must be called here.
  // This gets called from every setup() that uses the app.
  return appSingleton;
}
